// Savings Cart Page - Shows optimized cart with savings opportunities
import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  TrendingDown,
  MapPin,
  Wrench,
  ThumbsUp,
} from 'lucide-react'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { Button, useToast } from '../../components/radix'
import { api } from '../../services/api'
import type { CartItem, SupplierListing } from '../../types'
import { toastClearanceRef } from '../../utils/heightVarRef'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

interface SmartCartItem extends CartItem {
  originalPrice: number
  savings: number
}

interface SmartCartBranch {
  supplierId: string | number
  branchId: string | number | null
  supplierName: string
  branchName: string
  distanceMiles: number | null
  items: SmartCartItem[]
  total: number
  originalTotal: number
  savings: number
}

// Get cart from sessionStorage
function getCart(): CartItem[] {
  try {
    const cart = sessionStorage.getItem('engineerCart')
    if (!cart) return []
    return JSON.parse(cart)
  } catch {
    return []
  }
}

// Save cart to sessionStorage
function saveCart(cart: CartItem[]) {
  sessionStorage.setItem('engineerCart', JSON.stringify(cart))
}

export function SmartCartPage() {
  const navigate = useNavigate()
  const { tenantSlug } = useParams()
  const location = useLocation()
  const { addToast } = useToast()

  const [session, setSession] = useState<EngineerSession | null>(null)
  const [cart, setCart] = useState<CartItem[]>([])
  const [fulfillmentType, setFulfillmentType] = useState<'pickup' | 'delivery'>('pickup')
  const [smartCart, setSmartCart] = useState<SmartCartBranch[]>([])
  const [isOptimizing, setIsOptimizing] = useState(false)

  // Load session and cart on mount
  useEffect(() => {
    const savedSession = sessionStorage.getItem('engineerSession')
    if (savedSession) {
      setSession(JSON.parse(savedSession))
    } else {
      navigate(`/${tenantSlug}/engineer`)
      return
    }

    const cartItems = getCart()
    if (cartItems.length === 0) {
      navigate(`/${tenantSlug}/engineer/cart`)
      return
    }
    setCart(cartItems)

    // Get fulfillment type from navigation state
    const state = location.state as { fulfillment?: 'pickup' | 'delivery' } | null
    if (state?.fulfillment) {
      setFulfillmentType(state.fulfillment)
    }
  }, [navigate, tenantSlug, location.state])

  // Fetch all master products for cart items
  const masterProductIds = [...new Set(cart.map((item) => item.masterProductId).filter(Boolean))]

  const { data: masterProducts = [] } = useQuery({
    queryKey: ['smart-cart-products', masterProductIds],
    queryFn: async () => {
      const products = await Promise.all(
        masterProductIds.map((id) => api.getMasterProduct(id!))
      )
      return products
    },
    enabled: !!session && masterProductIds.length > 0,
  })

  // Optimize cart when data loads or fulfillment changes
  useEffect(() => {
    if (cart.length === 0 || masterProducts.length === 0) return

    setIsOptimizing(true)

    // Build map of masterProductId -> all supplier listings
    const productListingsMap = new Map<string, SupplierListing[]>()
    masterProducts.forEach((product) => {
      if (product && product.id) {
        productListingsMap.set(product.id, product.supplierListings)
      }
    })

    optimizeByBestPrice(cart, productListingsMap, fulfillmentType)

    setIsOptimizing(false)
  }, [cart, masterProducts, fulfillmentType])

  // Optimization: Best Price - find cheapest option for each item that supports selected fulfillment
  const optimizeByBestPrice = (
    cartItems: CartItem[],
    listingsMap: Map<string, SupplierListing[]>,
    fulfillment: 'pickup' | 'delivery'
  ) => {
    const optimizedItems: SmartCartItem[] = []

    cartItems.forEach((item) => {
      if (!item.masterProductId) {
        // If no master product, keep original if it supports fulfillment
        const option = item.fulfillmentOptions?.find((opt) => opt.type === fulfillment)
        if (option?.available && option.stockQuantity > 0) {
          optimizedItems.push({
            ...item,
            originalPrice: item.price,
            savings: 0,
          })
        }
        return
      }

      const listings = listingsMap.get(item.masterProductId)
      if (!listings || listings.length === 0) {
        const option = item.fulfillmentOptions?.find((opt) => opt.type === fulfillment)
        if (option?.available && option.stockQuantity > 0) {
          optimizedItems.push({
            ...item,
            originalPrice: item.price,
            savings: 0,
          })
        }
        return
      }

      // Filter listings that support selected fulfillment
      const availableListings = listings.filter((listing) => {
        const option = listing.fulfillmentOptions?.find((opt) => opt.type === fulfillment)
        return option?.available && option.stockQuantity > 0
      })

      if (availableListings.length === 0) {
        // No listings support this fulfillment, skip this item
        return
      }

      // Find cheapest listing among available ones
      const cheapest = availableListings.reduce((min, listing) =>
        listing.price < min.price ? listing : min
      )

      optimizedItems.push({
        productId: cheapest.productId,
        branchId: cheapest.branchId,
        supplierId: cheapest.supplierId,
        productName: cheapest.productName,
        supplierName: cheapest.supplierName,
        branchName: cheapest.branchName,
        supplierProductCode: cheapest.supplierProductCode,
        price: cheapest.price,
        quantity: item.quantity,
        maxStock: cheapest.stockQuantity,
        unitOfMeasure: cheapest.unitOfMeasure,
        distanceMiles: cheapest.distanceMiles,
        masterProductId: item.masterProductId,
        imageUrl: cheapest.imageUrl || undefined,
        fulfillmentOptions: cheapest.fulfillmentOptions,
        originalPrice: item.price,
        savings: (item.price - cheapest.price) * item.quantity,
      })
    })

    groupByBranch(optimizedItems)
  }


  // Group optimized items by branch
  const groupByBranch = (items: SmartCartItem[]) => {
    const branches = new Map<string, SmartCartBranch>()

    items.forEach((item) => {
      const key = `${item.supplierId}-${item.branchId}`

      if (!branches.has(key)) {
        branches.set(key, {
          supplierId: item.supplierId,
          branchId: item.branchId,
          supplierName: item.supplierName,
          branchName: item.branchName,
          distanceMiles: item.distanceMiles,
          items: [],
          total: 0,
          originalTotal: 0,
          savings: 0,
        })
      }

      const branch = branches.get(key)!
      branch.items.push(item)
      branch.total += item.price * item.quantity
      branch.originalTotal += item.originalPrice * item.quantity
      branch.savings += item.savings
    })

    // Sort by distance
    const sorted = Array.from(branches.values()).sort((a, b) => {
      if (a.distanceMiles === null) return 1
      if (b.distanceMiles === null) return -1
      return a.distanceMiles - b.distanceMiles
    })

    setSmartCart(sorted)
  }

  // Calculate totals
  const currentTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const smartTotal = smartCart.reduce((sum, branch) => sum + branch.total, 0)
  const totalSavings = currentTotal - smartTotal

  // Replace current cart with smart cart
  const handleReplaceCart = () => {
    const newCart: CartItem[] = []

    smartCart.forEach((branch) => {
      branch.items.forEach((item) => {
        newCart.push({
          productId: item.productId,
          branchId: item.branchId,
          supplierId: item.supplierId,
          productName: item.productName,
          supplierName: item.supplierName,
          branchName: item.branchName,
          supplierProductCode: item.supplierProductCode,
          price: item.price,
          quantity: item.quantity,
          maxStock: item.maxStock,
          unitOfMeasure: item.unitOfMeasure,
          distanceMiles: item.distanceMiles,
          masterProductId: item.masterProductId,
          imageUrl: item.imageUrl,
          fulfillmentOptions: item.fulfillmentOptions,
        })
      })
    })

    saveCart(newCart)
    window.dispatchEvent(new CustomEvent('cartUpdated'))

    addToast(
      `Basket optimised! Saved £${totalSavings.toFixed(2)}`,
      'success',
      3000
    )

    navigate(`/${tenantSlug}/engineer/cart`)
  }

  if (!session) {
    return null
  }

  return (
    <EngineerLayout hideHeader>
      {/* Header — themed via `.bg-nav` / `.text-nav`. Shared EngineerHeader
       * suppressed: the back+title bar is the only header on this page. */}
      <div className="bg-nav text-nav border-b border-nav-divider sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/${tenantSlug}/engineer/cart`)}
              className="p-2 -ml-2 text-nav hover:bg-nav-hover rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-green-600" />
              <h1 className="text-nav font-bold">Savings basket</h1>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Savings Summary */}
        <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-green-50 border-2 border-green-400 rounded-2xl p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-700 text-sm font-medium">Your current basket:</span>
            <span className="text-gray-900 font-semibold line-through decoration-2 decoration-gray-400">
              £{currentTotal.toFixed(2)}
            </span>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-green-800 text-base font-semibold">Optimised price:</span>
            <span className="text-green-700 font-bold text-2xl">£{smartTotal.toFixed(2)}</span>
          </div>
          <div className="bg-green-600 text-white rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="font-bold flex items-center gap-2">
              <ThumbsUp className="w-5 h-5" />
              Total Savings:
            </span>
            <span className="font-bold text-2xl">
              £{totalSavings.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Optimized Cart Items by Branch */}
        {isOptimizing ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
          </div>
        ) : (
          <div className="space-y-6 mb-6">
            {smartCart.map((branch) => {
              // Check if this branch has any savings
              const hasSavings = branch.savings > 0

              return (
                <div
                  key={`${branch.supplierId}-${branch.branchId}`}
                  className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  {/* Branch Header */}
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-gray-900 font-bold text-base mb-1">
                          {branch.supplierName}
                        </h3>
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <span>{branch.branchName}</span>
                          {branch.distanceMiles != null && (
                            <>
                              <span>•</span>
                              <MapPin className="w-3.5 h-3.5 text-brand-primary" />
                              <span>{branch.distanceMiles.toFixed(1)} mi away</span>
                            </>
                          )}
                        </div>
                      </div>

                      {hasSavings && (
                        <div className="bg-green-600 text-white px-3 py-1 rounded-lg text-xs font-bold ml-2">
                          Save £{branch.savings.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Branch Items */}
                  <div className="divide-y divide-gray-100">
                    {branch.items.map((item) => {
                      const hasItemSavings = item.savings > 0

                      return (
                        <div
                          key={`${item.productId}-${item.branchId}`}
                          className={`p-4 flex gap-4 ${
                            hasItemSavings ? 'bg-green-50/30' : ''
                          }`}
                        >
                          {/* Product Image */}
                          <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {item.imageUrl ? (
                              <img
                                src={item.imageUrl}
                                alt={item.productName}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <Wrench className="w-6 h-6 text-gray-300" />
                            )}
                          </div>

                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-gray-900 font-semibold text-sm mb-1">
                              {item.productName}
                            </h4>
                            <p className="text-gray-400 text-xs mb-2">{item.supplierProductCode}</p>

                            {/* Price Comparison */}
                            <div className="flex items-center gap-2 mb-1">
                              {hasItemSavings ? (
                                <>
                                  <span className="text-gray-400 text-sm line-through">
                                    £{item.originalPrice.toFixed(2)}
                                  </span>
                                  <span className="text-green-700 font-bold text-base">
                                    £{item.price.toFixed(2)}
                                  </span>
                                </>
                              ) : (
                                <span className="text-gray-900 font-bold text-base">
                                  £{item.price.toFixed(2)}
                                </span>
                              )}
                            </div>

                            {/* Savings Badge */}
                            {hasItemSavings && (
                              <div className="inline-flex items-center gap-1 bg-green-600 text-white px-2 py-0.5 rounded-md text-xs font-bold">
                                <ThumbsUp className="w-3 h-3" />
                                Save £{item.savings.toFixed(2)}
                              </div>
                            )}

                            <div className="text-gray-600 text-sm mt-1">Qty: {item.quantity}</div>
                          </div>

                          {/* Item Total */}
                          <div className="text-right">
                            <div className="text-gray-900 font-bold text-base">
                              £{(item.price * item.quantity).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Branch Subtotal */}
                  <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 text-sm">Subtotal</span>
                      <span className="text-gray-900 font-bold text-lg">
                        £{branch.total.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>

      {/* Sticky Footer */}
      <div ref={toastClearanceRef} className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex gap-3">
            <Button
              onClick={() => navigate(`/${tenantSlug}/engineer/cart`)}
              variant="outline"
              size="md"
              className="flex-1"
            >
              Keep current
            </Button>
            <Button
              onClick={handleReplaceCart}
              size="md"
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              Replace basket
            </Button>
          </div>
        </div>
      </div>
    </EngineerLayout>
  )
}
